<template>
    <div class="column-mapping">
        <div v-if="!saving">
            <v-card outlined class="mb-6">
                <v-card-title>Select Your Sheet & Options</v-card-title>
                <v-card-text>
                    <p class="body-1">Choose the sheet that contains your data and confirm the report options.</p>
                    <v-select v-model="selectedSheetName" :items="sheetOptions" item-text="label" item-value="value"
                        label="Select an Excel Sheet" outlined dense @change="onSheetChanged" class="mb-2"
                        :disabled="sheetOptions.length === 0">
                        <template v-slot:item="{ item }">
                            <div>
                                <div>{{ item.label }}</div>
                                <div class="text-caption grey--text">
                                    Rows: {{ item.row_count || 0 }}, Columns: {{ item.column_count || 0 }}
                                </div>
                                <div v-if="item.guardrail_message" class="text-caption error--text">
                                    {{ item.guardrail_message }}
                                </div>
                            </div>
                        </template>
                    </v-select>

                    <v-alert v-if="selectedSheetGuardrailMessage" type="error" outlined dense class="mb-4">
                        {{ selectedSheetGuardrailMessage }}
                    </v-alert>


                    <v-switch v-model="hasHeaderRow" inset dense color="primary"
                        :label="hasHeaderRow ? 'This file has a header row' : 'This file does not have a header row'"
                        @change="emitMappingChanged" />
                </v-card-text>
            </v-card>

            <div v-if="selectedSheetName && !selectedSheetGuardrailMessage">
                <v-card v-if="isSampleLoading" outlined class="mb-6 pa-6 text-center">
                    <v-progress-circular indeterminate color="primary" size="48" class="mb-3"></v-progress-circular>
                    <div class="text-subtitle-1 font-weight-medium">Loading preview sample data...</div>
                    <div class="grey--text">Hang tight while we process the selected sheet. This can take a bit depending on sheet complexity.</div>
                </v-card>
                <v-card v-else-if="rawImportRows.length > 0" outlined class="mb-6">
                    <v-card-title>Review Your Data Preview</v-card-title>
                    <v-card-text>
                        <p class="body-1">Here are the first {{ rawImportRows.length }} rows from your file. Does this look
                            correct?</p>
                        <div class="raw-import-table-container">
                            <table class="raw-import-table">
                                <thead>
                                    <tr>
                                        <th v-for="header in availableColumns" :key="header" class="raw-header">
                                            {{ header }}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(row, index) in rawImportRows" :key="index" class="raw-row">
                                        <td v-for="header in availableColumns" :key="header" class="raw-cell">
                                            {{ row[header] || '-' }}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </v-card-text>
                </v-card>

                <v-card outlined>
                    <v-card-title>Confirm Column Mappings</v-card-title>
                    <v-card-text>
                        <p class="body-1">
                            Match your file's columns to the correct RedChirp fields. You can change any of the suggested mappings.
                        </p>
                        <v-alert v-if="autoMappedFields.size > 0" type="info" outlined dense class="mb-4">
                            Fields marked with <v-chip x-small color="success" text-color="white" class="mx-1">
                                <v-icon x-small left>mdi-auto-fix</v-icon>Auto
                            </v-chip> were automatically mapped for you.
                        </v-alert>

                        <v-alert v-if="showValidationHints && unmappedRequiredFields.length > 0" type="error" outlined dense class="mb-4">
                            <strong>Required fields not yet mapped:</strong>
                            <span v-for="(field, index) in unmappedRequiredFields" :key="field.key" class="ml-1">
                                {{ field.label }}<span v-if="index < unmappedRequiredFields.length - 1">, </span>
                            </span>
                        </v-alert>

                        <v-alert v-if="showValidationHints && requiredFields.length > 0 && unmappedRequiredFields.length === 0" type="success"
                            outlined dense class="mb-4">
                            <strong>All required fields are mapped!</strong> You're ready to proceed.
                        </v-alert>

                        <div class="column-stats" v-show="!column_search">
                            <span class="column-count" v-if="showUnmatchedOnly">{{ filteredDisplayedColumns.length }} columns left to map.</span>
                            <span v-else class="column-count">{{ mappedColumnsCount }}/{{ filteredDisplayedColumns.length }} columns will be imported.</span>
                            <div class="d-flex align-center">
                                <div class="show-unmatched-toggle">
                                    <v-switch v-model="showUnmatchedOnly" inset dense color="primary" class="ma-0 pa-0"
                                        :label="showUnmatchedOnly ? 'Showing unmatched only' : 'Show unmatched columns only'" />
                                </div>
                            </div>
                        </div>

                        <v-text-field
                            v-model="column_search"
                            label="Search your file columns..."
                            prepend-inner-icon="mdi-magnify"
                            clearable
                            dense
                            outlined
                            hide-details
                            class="mb-4"
                        />

                        <div class="mapping-table-container mapping-table-scrollable">
                            <table class="mapping-table">
                                <thead>
                                    <tr>
                                        <th class="file-col">Your File Column</th>
                                        <th class="arrow-col"></th>
                                        <th class="field-col">RedChirp Field</th>
                                        <th class="preview-col">Example Data
                                            <v-tooltip top>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon color="primary" small v-bind="attrs" v-on="on" class="ml-1">mdi-information-outline</v-icon>
                                                </template>
                                                <span>We scan your file for the first non-empty row to show as an example.</span>
                                            </v-tooltip>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="column in filteredDisplayedColumns" :key="column.name" class="mapping-row">
                                        <td class="file-cell">
                                            <div class="file-column-name">{{ column.name }}</div>
                                        </td>
                                        <td class="arrow-cell">
                                            <v-icon color="primary" class="mapping-arrow">mdi-arrow-right</v-icon>
                                        </td>
                                        <td class="field-cell">
                                            <v-select v-model="column.mappedField"
                                                :items="getOptionsForColumn(column.name)" item-text="label"
                                                item-value="key" placeholder="Search or select a field..." outlined dense
                                                clearable hide-details="auto"
                                                @change="onFieldMappingChanged(column.name, $event)"
                                                class="field-select">
                                                <template v-slot:prepend-item>
                                                    <v-subheader class="field-group-header">AVAILABLE FIELDS</v-subheader>
                                                    <v-divider class="mb-2" />
                                                </template>

                                                <template v-slot:item="{ item }">
                                                    <div class="field-option">
                                                        <div class="field-name">
                                                            {{ item.label }}
                                                            <span v-if="item.required" class="field-required-indicator">*</span>
                                                        </div>
                                                        <div v-if="item.required" class="field-required">Required</div>
                                                    </div>
                                                </template>

                                                <template v-slot:selection="{ item }">
                                                    <div class="field-selection-container">
                                                        <span>{{ item.label }}</span>
                                                        <v-chip v-if="isAutoMapped(item.key)" x-small color="success"
                                                            text-color="white" class="auto-pill ml-2">
                                                            <v-icon x-small left>mdi-auto-fix</v-icon>Auto
                                                        </v-chip>
                                                    </div>
                                                </template>
                                            </v-select>
                                        </td>
                                        <td class="preview-cell">
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <div class="preview-data" v-bind="attrs" v-on="on">
                                                        {{ getSampleDataForColumn(column.name, true) }}
                                                    </div>
                                                </template>
                                                <span>{{ getSampleDataForColumn(column.name, false) || 'No data available' }}</span>
                                            </v-tooltip>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <!-- Technically have support for contact-list as well but didn't see the use case at time of writing. To add support, add 'contact-list' to the array below; should already work -->
                        <div v-if="['bulk-message-recipients'].includes(importType)" class="mt-4">
                            <rc-text-expand-button
                                :expanded="advanced_settings_expanded"
                                @click="advanced_settings_expanded = !advanced_settings_expanded"
                            >
                                Advanced Settings
                            </rc-text-expand-button>

                            <div v-show="advanced_settings_expanded" class="mt-2 pl-4">
                                <v-subheader class="pl-0">Extra Custom Fields</v-subheader>
                                <p class="text-caption grey--text mb-4">
                                    Need to import more data to utilize in your bulk message? Add up to 10 extra columns from your sheet that can be imported to use as variables in your messages.
                                </p>

                                <div >
                                    <div v-for="(field, index) in extraFieldDefinitions" :key="field.key" class="d-flex align-center mb-2">
                                        <v-autocomplete
                                            v-model="columnMappings[field.key]"
                                            :items="availableColumns"
                                            label="Select Extra Field Column"
                                            outlined
                                            dense
                                            hide-details
                                            class="mr-2 flex-grow-1"
                                            placeholder="Search or select a column..."
                                            @change="emitMappingChanged"
                                            clearable
                                        ></v-autocomplete>
                                        <v-btn icon color="error" @click="removeCustomField(index)">
                                            <v-icon>mdi-minus-circle</v-icon>
                                        </v-btn>
                                    </div>
                                </div>

                                <div class="d-flex justify-end mt-4">
                                    <v-btn
                                        color="primary"
                                        @click="addCustomField"
                                        :disabled="extraFieldDefinitions.length >= 10"
                                    >
                                        <v-icon left>mdi-plus</v-icon>
                                        Add Custom Field
                                    </v-btn>
                                </div>
                            </div>
                        </div>

                    </v-card-text>
                </v-card>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'column-mapping-component',

    props: {
        availableSheets: { type: Array, required: true },
        sheetMetadataMap: { type: Object, default: () => ({}) },
        requiredFields: { type: Array, required: true },
        optionalFields: { type: Array, default: () => [] },
        suggestedMapping: { type: Object, default: () => ({}) },
        sampleData: { type: Array, default: () => [] },
        saving: { type: Boolean, default: false },
        importType: { type: String, required: true }
    },

    data() {
        return {
            selectedSheetName: null,
            columnMappings: {},
            autoMappedFields: new Set(),
            showUnmatchedOnly: false,
            hasHeaderRow: true,
            autoMappedSheets: new Set(),
            extraFieldDefinitions: [],
            advanced_settings_expanded: false,
            column_search: '',
        }
    },

    mounted() {
        this.$nextTick(() => {
            this.emitMappingChanged();
        });
    },

    computed: {
        sheetOptions() {
            return this.availableSheets.map(sheet => {
                const meta = this.sheetMetadataMap ? this.sheetMetadataMap[sheet.sheet_name] : null;
                const row_count = meta?.row_count ?? (typeof sheet.row_count === 'number' ? sheet.row_count : sheet.total_rows);
                const column_count = meta?.column_count ?? (typeof sheet.column_count === 'number'
                    ? sheet.column_count
                    : (sheet.column_headers ? sheet.column_headers.length : 0));
                const guardrail_message = meta?.guardrail_message || sheet.guardrail_message || null;
                return {
                    label: sheet.sheet_name,
                    value: sheet.sheet_name,
                    row_count,
                    column_count,
                    guardrail_message
                };
            });
        },

        selectedSheet() {
            if (!this.selectedSheetName) {
                return null;
            }
            const sheet = this.availableSheets.find(sheet => sheet.sheet_name === this.selectedSheetName);
            if (sheet) {
                return sheet;
            }
            const meta = this.sheetMetadataMap ? this.sheetMetadataMap[this.selectedSheetName] : null;
            if (meta) {
                return {
                    sheet_name: this.selectedSheetName,
                    row_count: meta.row_count,
                    column_count: meta.column_count,
                    guardrail_message: meta.guardrail_message
                };
            }
            return null;
        },

        selectedSheetGuardrailMessage() {
            if (this.selectedSheet) {
                return this.selectedSheet.guardrail_message || null;
            }
            if (this.selectedSheetName && this.sheetMetadataMap) {
                return this.sheetMetadataMap[this.selectedSheetName]?.guardrail_message || null;
            }
            return null;
        },

        isSampleLoading() {
            if (!this.selectedSheetName) {
                return false;
            }
            if (this.selectedSheetGuardrailMessage) {
                return false;
            }
            const sheetSampleData = this.sampleData.find(sheet => sheet.sheet_name === this.selectedSheetName);
            if (!sheetSampleData) {
                return true;
            }
            return !sheetSampleData.column_headers || sheetSampleData.column_headers.length === 0;
        },

        showValidationHints() {
            return !!this.selectedSheetName && !this.isSampleLoading;
        },

        allFields() {
            return [...this.requiredFields, ...this.optionalFields, ...this.extraFieldDefinitions];
        },

        computedColumnHeaders() {
            if (!this.selectedSheetName) return [];
            const sheet = this.availableSheets.find(s => s.sheet_name === this.selectedSheetName);
            if (!sheet || !sheet.column_headers) return [];
            const count = sheet.column_headers.length;
            if (this.hasHeaderRow) {
                return sheet.column_headers;
            }
            // Generate Excel-style letters: A, B, ..., Z, AA, AB, ... up to count
            const headers = [];
            for (let i = 0; i < count; i++) {
                headers.push(this.indexToLetters(i));
            }
            return headers;
        },

        availableColumns() {
            if (!this.computedColumnHeaders.length) return [];
            return this.computedColumnHeaders.filter(columnName => {
                const lowerName = columnName.toLowerCase();
                return !(lowerName.includes('row') && lowerName.includes('number'));
            });
        },

        rawImportRows() {
            if (!this.selectedSheetName) return [];
            const sheetSampleData = this.sampleData.find(sheet => sheet.sheet_name === this.selectedSheetName);
            if (!sheetSampleData || !sheetSampleData.sample_rows) return [];

            const headers = this.computedColumnHeaders;
            const sampleData = sheetSampleData.sample_rows.slice(0, 10);
            return sampleData.map(row => {
                const rowData = {};
                headers.forEach((header, index) => {
                    rowData[header] = row[index.toString()] || '';
                });
                return rowData;
            });
        },

        canContinueToPreview() {
            if (this.selectedSheetGuardrailMessage) {
                return false;
            }
            return this.showValidationHints && this.requiredFields.every(field => {
                const mapping = this.columnMappings[field.key];
                return mapping && mapping !== '' && (Array.isArray(mapping) ? mapping.length > 0 : true);
            });
        },

        displayedColumns() {
            if (!this.availableColumns.length) return [];
            return this.availableColumns
                .filter(columnName => {
                    const lowerName = columnName.toLowerCase();
                    return !(lowerName.includes('row') && lowerName.includes('number')) && !lowerName.match(/^(row|#|index|line)(\s*#|\s*number|\s*num)?$/);
                })
                .map(columnName => ({
                    name: columnName,
                    mappedField: this.findMappedField(columnName)
                }))
                .filter(column => this.showUnmatchedOnly ? !column.mappedField : true);
        },

        filteredDisplayedColumns() {
            if (!this.column_search || this.column_search.trim() === '') {
                return this.displayedColumns;
            }
            const searchTerm = this.column_search.toLowerCase().trim();
            return this.displayedColumns.filter(column => 
                column.name.toLowerCase().includes(searchTerm)
            );
        },

        fieldOptions() {
            return this.allFields.map(field => ({
                key: field.key,
                label: field.label,
                required: field.required || false,
                type: field.type || 'text',
                is_extra: field.is_extra || false
            }));
        },

        mappedColumnsCount() {
            return this.availableColumns.filter(column => this.findMappedField(column)).length;
        },

        unmappedRequiredFields() {
            return this.requiredFields.filter(field => {
                const mapping = this.columnMappings[field.key];
                return !mapping || mapping === '' || (Array.isArray(mapping) && mapping.length === 0);
            });
        }
    },

    watch: {
        columnMappings: { handler() { this.emitMappingChanged() }, deep: true },
        selectedSheetName() { this.emitMappingChanged() },

        canContinueToPreview(newValue) {
            this.$emit('can-continue-changed', newValue);
        },

        suggestedMapping: {
            immediate: true,
            handler(newMapping) {
                if (newMapping && newMapping.selected_sheet_name) {
                    this.selectedSheetName = newMapping.selected_sheet_name;
                    const mappings = {};
                    if (Array.isArray(newMapping.column_mappings)) {
                        newMapping.column_mappings.forEach(mapping => {
                            mappings[mapping.field_key] = mapping.column_name;
                            this.autoMappedFields.add(mapping.field_key);
                        });
                        this.columnMappings = mappings;
                    } else {
                        this.columnMappings = { ...newMapping.column_mappings };
                        Object.entries(this.columnMappings).forEach(([fieldKey, columnName]) => {
                            if (columnName) this.autoMappedFields.add(fieldKey);
                        });
                    }
                    this.$nextTick(() => this.$forceUpdate());
                }
            }
        },

        availableSheets: {
            immediate: true,
            handler(newSheets, oldSheets) {
                if (newSheets && oldSheets) {
                    const newSheetNames = new Set(newSheets.map(sheet => sheet.sheet_name));
                    const oldSheetNames = new Set(oldSheets.map(sheet => sheet.sheet_name));
                    const hasSameNames = newSheetNames.size === oldSheetNames.size && [...newSheetNames].every(name => oldSheetNames.has(name));
                    if (!hasSameNames) {
                        this.selectedSheetName = null;
                        this.autoMappedSheets.clear();
                    }
                }

                if (newSheets && newSheets.length === 1 && !this.selectedSheetName) {
                    this.selectedSheetName = newSheets[0].sheet_name;
                    this.$nextTick(() => this.triggerSheetChanged());
                }

                if (!newSheets || !this.selectedSheetName) {
                    return;
                }

                const currentSheet = newSheets.find(sheet => sheet.sheet_name === this.selectedSheetName);
                if (!currentSheet) {
                    return;
                }

                if (!currentSheet.column_headers || currentSheet.column_headers.length === 0) {
                    return;
                }

                if (this.autoMappedSheets.has(currentSheet.sheet_name)) {
                    return;
                }

                this.autoMappedSheets.add(currentSheet.sheet_name);
                this.$nextTick(() => this.generateSmartMappingsForSheet());
            }
        },

        hasHeaderRow() {
            // Reset mappings when header mode changes
            this.onSheetChanged();
        }
    },

    methods: {
        onSheetChanged() {
            this.columnMappings = {};
            this.autoMappedFields = new Set();
            if (this.selectedSheetName) {
                this.autoMappedSheets.delete(this.selectedSheetName);
            }
            if (this.selectedSheetGuardrailMessage) {
                this.triggerSheetChanged();
                return;
            }
            this.generateSmartMappingsForSheet();
            this.triggerSheetChanged();
        },

        triggerSheetChanged() {
            if (!this.selectedSheetName) {
                return;
            }
            if (this.selectedSheetGuardrailMessage) {
                return;
            }
            this.$emit('sheet-changed', {
                sheet_name: this.selectedSheetName,
                has_header_row: this.hasHeaderRow
            });
        },

        generateSmartMappingsForSheet() {
            if (!this.selectedSheetName) return;
            if (this.selectedSheetGuardrailMessage) return;
            const selectedSheet = this.availableSheets.find(sheet => sheet.sheet_name === this.selectedSheetName);
            if (!selectedSheet) return;

            if (!selectedSheet.column_headers || selectedSheet.column_headers.length === 0) {
                return;
            }

            const mappings = {};
            const autoMapped = new Set();
            const columnHeaders = this.computedColumnHeaders;
            const usedColumns = new Set();

            this.allFields.forEach(field => {
                let matched = null;
                if (field.is_extra) {
                    // Don't auto-map extra fields, they are user-defined
                    matched = null;
                } else if (field.multiple) {
                    matched = this.findAllColumnMatches(columnHeaders, field.key, usedColumns);
                } else {
                    matched = this.findBestColumnMatch(columnHeaders, field.key, usedColumns);
                }

                if (matched && (Array.isArray(matched) ? matched.length > 0 : true)) {
                    mappings[field.key] = matched;
                    autoMapped.add(field.key);
                    (Array.isArray(matched) ? matched : [matched]).forEach(col => usedColumns.add(col));
                }
            });
            this.columnMappings = mappings;
            this.autoMappedFields = autoMapped;
        },

        findAllColumnMatches(columnHeaders, fieldKey, usedColumns = new Set()) {
            const patterns = this.getFieldPatterns();
            const field_patterns = patterns[fieldKey] || [];
            const matchedColumns = [];
            for (const header of columnHeaders) {
                if (usedColumns.has(header)) continue;
                for (const pattern of field_patterns) {
                    // Use word boundary matching to prevent false positives (e.g., "cell" in "ecellar")
                    const escaped_pattern = pattern.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    const word_boundary_regex = new RegExp(`(^|[^a-z])${escaped_pattern}([^a-z]|$)`, 'i');
                    if (word_boundary_regex.test(header.toLowerCase())) {
                        matchedColumns.push(header);
                        break;
                    }
                }
            }
            return matchedColumns;
        },

        getFieldPatterns() {
            return {
                'phone_number': ['phone number', 'phone_number', 'phone', 'mobile', 'cell', 'telephone'],
                'first_name': ['first name', 'first_name', 'fname', 'given name', 'first'],
                'last_name': ['last name', 'last_name', 'lname', 'surname', 'family name', 'last'],
                'consent_date': ['consent date', 'consent_date', 'date', 'timestamp'],
                'specimen': ['specimen', 'sample', 'type'],
                'full_name': ['full name', 'full_name', 'customer name', 'complete name', 'name'],
                'conversational': ['conversational', 'conversation', 'conv'],
                'informational': ['informational', 'information', 'info'],
                'promotional': ['promotional', 'promotion', 'promo', 'marketing'],
                'email': ['email', 'email address', 'e-mail', 'mail'],
                'birthdate': ['birthdate', 'birth date', 'birthday', 'birth_date', 'date of birth', 'dob', 'born'],
            };
        },

        findBestColumnMatch(columnHeaders, fieldKey, usedColumns = new Set()) {
            const patterns = this.getFieldPatterns();
            const field_patterns = patterns[fieldKey] || [fieldKey];
            for (const pattern of field_patterns) {
                const exactMatch = columnHeaders.find(h => !usedColumns.has(h) && h.toLowerCase() === pattern.toLowerCase());
                if (exactMatch) return exactMatch;
            }
            for (const pattern of field_patterns) {
                // Use word boundary matching to prevent false positives (e.g., "cell" in "ecellar")
                const escaped_pattern = pattern.toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const word_boundary_regex = new RegExp(`(^|[^a-z])${escaped_pattern}([^a-z]|$)`, 'i');
                const partial_match = columnHeaders.find(h => !usedColumns.has(h) && word_boundary_regex.test(h.toLowerCase()));
                if (partial_match) return partial_match;
            }
            return null;
        },

        getOptionsForColumn(columnName) {
            const mappedFieldKeys = new Set(
                Object.entries(this.columnMappings)
                    .filter(([, value]) => value && value !== columnName)
                    .map(([key]) => key)
            );

            return this.fieldOptions
                .filter(opt => !opt.is_extra)
                .map(opt => {
                    const field = this.allFields.find(f => f.key === opt.key);
                    const isUsedElsewhere = !field.multiple && mappedFieldKeys.has(opt.key);
                    return { ...opt, disabled: isUsedElsewhere };
                });
        },

        getSampleDataForColumn(columnName, truncate = true) {
            if (!this.findMappedField(columnName)) return '-';
            if (!this.selectedSheetName) return 'No preview';

            const sheetSampleData = this.sampleData.find(sheet => sheet.sheet_name === this.selectedSheetName);
            if (!sheetSampleData || !sheetSampleData.sample_rows || sheetSampleData.sample_rows.length === 0) return 'No preview';

            const columnIndex = this.computedColumnHeaders.indexOf(columnName);
            if (columnIndex === -1) return 'No data';

            for (const row of sheetSampleData.sample_rows) {
                const value = row[columnIndex.toString()];
                if (value && value.toString().trim() !== '') {
                    const valueStr = value.toString();
                    return truncate && valueStr.length > 20 ? valueStr.substring(0, 20) + '...' : valueStr;
                }
            }
            return 'No data';
        },

        findMappedField(columnName) {
            for (const [fieldKey, mappedColumn] of Object.entries(this.columnMappings)) {
                if (Array.isArray(mappedColumn) ? mappedColumn.includes(columnName) : mappedColumn === columnName) {
                    return fieldKey;
                }
            }
            return null;
        },

        onFieldMappingChanged(columnName, fieldKey) {
            const targetField = this.allFields.find(field => field.key === fieldKey);

            // Clear previous mappings for this column
            Object.keys(this.columnMappings).forEach(key => {
                const mapping = this.columnMappings[key];
                if (Array.isArray(mapping)) {
                    this.$set(this.columnMappings, key, mapping.filter(col => col !== columnName));
                } else if (mapping === columnName) {
                    this.$set(this.columnMappings, key, null);
                }
            });

            // Set new mapping
            if (fieldKey) {
                if (targetField && targetField.multiple) {
                    const current = this.columnMappings[fieldKey] || [];
                    this.$set(this.columnMappings, fieldKey, [...current, columnName]);
                } else {
                    this.$set(this.columnMappings, fieldKey, columnName);
                }
                this.autoMappedFields.delete(fieldKey);
            }
        },

        emitMappingChanged() {
            if (!this.selectedSheetName) {
                this.$emit('mapping-changed', null);
                return;
            }
            try {
                this.$emit('mapping-changed', this.buildImportDefinition());
            } catch (error) {
                this.$emit('mapping-changed', null);
            }
        },

        buildImportDefinition() {
            let definition = null;
            if (this.importType === 'consent') {
                definition = this.buildConsentImportDefinition();
            } else if (['contact', 'contact-list', 'bulk-message-recipients'].includes(this.importType)) {
                definition = this.buildContactImportDefinition();
            }
            if (!definition || !definition.sheet_name) throw new Error('Invalid definition');
            return definition;
        },

        buildConsentImportDefinition() {
            return {
                sheet_name: this.selectedSheetName,
                has_header_row: this.hasHeaderRow,
                phone_number_column: this.columnMappings.phone_number || '',
                consent_date_column: this.columnMappings.consent_date,
                specimen_column: this.columnMappings.specimen,
                full_name_column: this.columnMappings.full_name || null,
                conversational_column: this.columnMappings.conversational || null,
                informational_column: this.columnMappings.informational || null,
                promotional_column: this.columnMappings.promotional || null,
            };
        },

        buildContactImportDefinition() {
            return {
                sheet_name: this.selectedSheetName,
                has_header_row: this.hasHeaderRow,
                first_name_column: this.columnMappings.first_name || '',
                last_name_column: this.columnMappings.last_name || '',
                phone_number_columns: Array.isArray(this.columnMappings.phone_number) ? this.columnMappings.phone_number.filter(Boolean) : [],
                email_column: this.columnMappings.email || '',
                birthdate_column: this.columnMappings.birthdate || '',
                notes_column: this.columnMappings.notes || '',
                extra_fields_columns: this.getExtraFieldsColumns()
            };
        },

        getExtraFieldsColumns() {
            const extraColumns = [];
            this.extraFieldDefinitions.forEach(field => {
                const mappedColumn = this.columnMappings[field.key];
                if (mappedColumn) {
                    if (Array.isArray(mappedColumn)) {
                        mappedColumn.forEach(col => extraColumns.push(col));
                    } else {
                        extraColumns.push(mappedColumn);
                    }
                }
            });
            return extraColumns;
        },



        addCustomField() {
            if (this.extraFieldDefinitions.length >= 10) return;
            
            // Generate a unique key
            const key = 'extra_field_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
            
            this.extraFieldDefinitions.push({
                key: key,
                label: 'Extra Field',
                required: false,
                type: 'text',
                is_extra: true
            });
        },

        removeCustomField(index) {
            const field = this.extraFieldDefinitions[index];
            if (field) {
                // Clear the mapping
                this.$delete(this.columnMappings, field.key);
                this.extraFieldDefinitions.splice(index, 1);
            }
        },

        isAutoMapped(fieldKey) {
            return this.autoMappedFields.has(fieldKey) && this.columnMappings[fieldKey];
        },

        // Convert 0-based index to Excel-style column letters
        indexToLetters(index) {
            let n = index + 1;
            let result = '';
            while (n > 0) {
                const rem = (n - 1) % 26;
                result = String.fromCharCode(65 + rem) + result;
                n = Math.floor((n - 1) / 26);
            }
            return result;
        },
    }
}
</script>

<style scoped>
.column-mapping {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}

.column-stats {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
}

.column-count {
    font-size: 14px;
    color: #333;
    font-weight: 500;
}

.raw-import-table-container {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: auto;
    background: white;
    max-height: 400px;
}

.raw-import-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}

.raw-import-table .raw-header {
    background-color: #f8f9fa;
    padding: 12px 8px;
    font-weight: 600;
    font-size: 12px;
    color: #333;
    border-bottom: 1px solid #e0e0e0;
    text-align: left;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 1;
}

.raw-import-table .raw-cell {
    padding: 8px;
    border-bottom: 1px solid #f0f0f0;
    border-right: 1px solid #f0f0f0;
    vertical-align: top;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.raw-import-table .raw-row:hover {
    background-color: #f8f9fa;
}

.mapping-table-container {
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    overflow: hidden;
    background: white;
}

.mapping-table-scrollable {
    max-height: 800px;
    overflow-y: auto;
}

.mapping-table {
    width: 100%;
    border-collapse: collapse;
}

.mapping-table thead th {
    background-color: #f8f9fa;
    padding: 16px 12px;
    font-weight: 600;
    font-size: 14px;
    color: #333;
    border-bottom: 1px solid #e0e0e0;
    text-align: left;
}

.mapping-table tbody td {
    padding: 16px 12px;
    border-bottom: 1px solid #f0f0f0;
    vertical-align: middle;
}

.mapping-table tbody tr:last-child td {
    border-bottom: none;
}

/* Column widths */
.file-col { width: 30%; }
.arrow-col { width: 40px; }
.field-col { width: 40%; }
.preview-col { width: 30%; }

.file-cell {
    background-color: #f5f5f5;
}

.file-column-name {
    font-weight: 500;
    color: #333;
    padding: 8px 12px;
    background-color: #e9ecef;
    border-radius: 4px;
    font-size: 14px;
}

.arrow-cell {
    text-align: center;
}

.mapping-arrow {
    font-size: 20px;
}

.field-select {
    margin-bottom: 0;
}

.field-group-header {
    font-weight: 600;
    color: #666;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.field-option {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
}

.field-name {
    font-weight: 500;
}

.field-required {
    font-size: 12px;
    color: var(--v-error-base);
    font-weight: 500;
}

.field-required-indicator {
    color: var(--v-error-base);
    font-weight: bold;
    margin-left: 2px;
}

.field-selection-container {
    display: flex;
    align-items: center;
    width: 100%;
}

.auto-pill {
    flex-shrink: 0;
}

.preview-cell {
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 13px;
}

.preview-data {
    color: #666;
    background-color: #f8f9fa;
    padding: 4px 8px;
    border-radius: 4px;
    font-size: 12px;
    display: inline-block;
}
</style>
